import { Control, Controller, FieldValues, Path } from "react-hook-form";
import TextField from "@mui/material/TextField";
import { ComponentProps, ReactNode, forwardRef } from "react";

export interface FormTextFieldProps<T extends FieldValues> extends ComponentProps<typeof TextField> {
  control: Control<T, any>;
  name: Path<T>;
  integerOnly?: boolean;
  disabledArrows?: boolean;
  disableZero?: boolean;
  allowDecimalComma?: boolean;
  endAdornment?: ReactNode;
}

const FormTextFieldInner = <T extends FieldValues>(
  {
    name, // name and control are used by Controller, not passed down directly here
    control: _control, // aliased to avoid passing to TextField if it's not a TextField prop
    onChange: propOnChange, // Renamed to avoid conflict with RHF's field.onChange
    helperText,
    integerOnly = false,
    disabledArrows = false,
    disableZero = false,
    allowDecimalComma = false,
    endAdornment,
    type,
    ...inputProps
  }: FormTextFieldProps<T>,
  ref: React.Ref<HTMLDivElement> // Added ref for forwardRef
) => (
  <Controller
    name={name}
    control={_control}
    render={({
      field: { value, onChange: rhfOnChange, ...fieldRest }, // Renamed RHF's onChange to rhfOnChange
      fieldState: { error },
      formState: { isSubmitted },
    }) => (
      <TextField
        type={type}
        color="primary"
        helperText={isSubmitted && !!error ? error?.message : helperText}
        error={isSubmitted && !!error}
        value={value ?? ""}
        onWheel={(e) => {
          if (type === "number") {
            (e.target as HTMLElement).blur();
          }
        }}
        onKeyDown={(e) => {
          if (["e", "-", "+"].includes(e.key.toLowerCase()) && (type === "number" || allowDecimalComma)) {
            e.preventDefault();
          }
          if (integerOnly && type === "number" && e.key === ".") {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          const originalValue = e.target.value;
          let processedValue = originalValue;

          if (allowDecimalComma) {
            const isValidDecimal = /^[0-9]*[.,]?[0-9]*$/.test(processedValue);
            if (!isValidDecimal) {
              // If not valid, perhaps revert or don't proceed.
              // For now, let's assume we only proceed if valid, or let RHF handle validation.
              // This part might need more robust handling depending on desired UX.
              // If we return here, RHF won't be updated.
            }
          }

          if (disableZero && processedValue === "0") {
            processedValue = ""; // Update processedValue, not e.target.value directly
          } else if (integerOnly && processedValue.length > 1 && processedValue.startsWith("0") && !processedValue.startsWith("0.")) {
             // Avoid stripping leading zero if it's like "0.5"
            processedValue = processedValue.slice(1);
          }
          
          // Update the event object if we changed the value
          if (e.target.value !== processedValue) {
            const newEvent = { ...e, target: { ...e.target, value: processedValue } };
            propOnChange?.(newEvent as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>); // Call original prop onChange
            rhfOnChange(newEvent); // Call RHF onChange
          } else {
            propOnChange?.(e);
            rhfOnChange(e);
          }
        }}
        {...fieldRest} // Spread rest of RHF field props (like onBlur, name, ref from controller if not using forwardRef directly)
        {...inputProps} // Spread other TextField props
        InputProps={{ // Spread existing InputProps from inputProps if any, then add endAdornment
          ...inputProps.InputProps,
          endAdornment,
        }}
        sx={{
          "& input[type=number]": {
            MozAppearance: disabledArrows && type === "number" ? "textfield" : undefined,
          },
          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
            WebkitAppearance: disabledArrows && type === "number" ? "none" : undefined,
            margin: disabledArrows && type === "number" ? 0 : undefined,
          },
          ...inputProps.sx, // Merge sx props
        }}
        ref={ref} // Apply the forwarded ref
      />
    )}
  />
);

const FormTextField = forwardRef(FormTextFieldInner) as <T extends FieldValues>(
  props: FormTextFieldProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof FormTextFieldInner>;

export default FormTextField;
